/**
 * SidebarItemContent — the pure visual content of a sidebar row.
 *
 * Extracted from SidebarItem so the same rendering can be used in multiple
 * contexts (sidebar NavLink, AI chat results, previews) — following the
 * same reuse pattern as TaskItem and ChecklistItem.
 *
 * Layout: [Icon 24px] [Label (flex-1)] [Flag+Date?] [Avatars?] [AlertBadge?] [Badge?] [Trailing?]
 */

import { memo, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import SolidFlagIcon from "@/shared/icons/totodoicons/solid/flag.svg?react"
import { MemberAvatars, type MemberInfo } from "./MemberAvatars"
import { cn } from "@/shared/utils/general/cn"
import { parseDbDate } from "@totodo/operations"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SidebarItemContentProps {
  icon: ReactNode
  label: string
  /** Show label in a dimmed style (for empty/untitled items) */
  labelEmpty?: boolean
  /** Badge count (plain number at end of row) */
  badge?: number
  /** Alert count — red circle badge (e.g. deadline count on Today) */
  alert?: number
  /** Due date timestamp — shows flag icon + relative date */
  dueAt?: string
  /** Member avatars for shared items */
  avatars?: MemberInfo[]
  /** Trailing element (e.g. collapse chevron for areas) */
  trailing?: ReactNode
  /** Red dot indicator (e.g. new tasks since last visit) */
  dot?: boolean
  /** Whether the parent item is selected/active (affects avatar contrast) */
  isActive?: boolean
  /** Visually dim this item (e.g. later / someday projects) */
  dimmed?: boolean
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getShortDeadlineText(dueAt: string): string {
  const now = new Date()
  const dueMs = parseDbDate(dueAt)
  if (dueMs === undefined) return ""
  const due = new Date(dueMs)

  const isToday =
    now.getFullYear() === due.getFullYear() &&
    now.getMonth() === due.getMonth() &&
    now.getDate() === due.getDate()
  if (isToday) return "Today"

  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const isTomorrow =
    tomorrow.getFullYear() === due.getFullYear() &&
    tomorrow.getMonth() === due.getMonth() &&
    tomorrow.getDate() === due.getDate()
  if (isTomorrow) return "Tmrw"

  const diffMs = due.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays < 0) {
    const absDays = Math.abs(diffDays)
    if (absDays > 90) return `${due.getFullYear()}`
    return `${absDays}d`
  }
  if (diffDays > 0) {
    if (diffDays > 90) return `${due.getFullYear()}`
    return `${diffDays}d`
  }
  return "Today"
}

const FlagIcon = memo(function FlagIcon({ overdue }: { overdue: boolean }) {
  return (
    <SolidFlagIcon
      width={14}
      height={14}
      className={overdue ? "text-destructive" : "text-muted-foreground"}
    />
  )
})

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SidebarItemContent = memo(function SidebarItemContent({
  icon,
  label,
  labelEmpty,
  badge,
  alert,
  dueAt,
  avatars,
  trailing,
  dot,
  isActive,
  dimmed,
}: SidebarItemContentProps) {
  const { t } = useTranslation()

  const dueMs = parseDbDate(dueAt)
  const isOverdue = dueMs !== undefined && dueMs <= Date.now()
  const dueText = dueAt ? getShortDeadlineText(dueAt) : undefined

  return (
    <>
      {/* Icon — 24×24 shrink-0, with optional new-task dot */}
      <span className="relative shrink-0">
        {icon}
        {dot && (
          <span
            className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-destructive"
            style={{ boxShadow: "0 0 0 1.5px var(--sidebar-surface, var(--sidebar))" }}
          />
        )}
      </span>

      {/* Label — flex-1, truncate with ellipsis */}
      <span
        className={cn(
          "min-w-0 flex-1 truncate",
          labelEmpty && "opacity-50",
          dimmed && "opacity-50",
        )}
      >
        {label}
      </span>

      {/* Due date flag (only on due items) */}
      {dueText && (
        <span
          className={cn(
            "flex shrink-0 items-center gap-0.5 text-xs font-medium whitespace-nowrap",
            isOverdue ? "text-destructive" : "text-muted-foreground",
          )}
        >
          <FlagIcon overdue={isOverdue} />
          {t(dueText)}
        </span>
      )}

      {/* Member avatars */}
      {avatars && avatars.length > 0 && (
        <MemberAvatars members={avatars} selected={isActive} />
      )}

      {/* Alert badge — red circle (deadline count) */}
      {alert != null && alert > 0 && (
        <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-destructive text-[12px] font-medium text-white">
          {alert}
        </span>
      )}

      {/* Badge count — plain number */}
      {badge != null && badge > 0 && (
        <span className="shrink-0 text-sm font-medium tabular-nums text-muted-foreground">
          {badge}
        </span>
      )}

      {/* Trailing element (e.g. area collapse chevron) */}
      {trailing}
    </>
  )
})
